import {
  Injectable,
  signal,
  computed,
  inject,
  Injector,
  WritableSignal,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { toObservable } from "@angular/core/rxjs-interop";
import {
  Observable,
  of,
  throwError,
  firstValueFrom,
  race,
  timer,
  from,
  defer,
  EMPTY,
  EmptyError,
  Subject,
} from "rxjs";
import {
  tap,
  catchError,
  map,
  switchMap,
  take,
  shareReplay,
  finalize,
  filter,
  timeout,
  takeUntil,
} from "rxjs/operators";
import { LoggerService } from "@core/services/logger.service";
import { CrossTabAuthService } from "@core/services/cross-tab-auth.service";
import { SharedAuthStateService } from "../../stores/shared-auth-state.service";
import { SentryService } from "@core/services/sentry.service";
import { AppStorageService } from "@core/services/app-storage.service";
import { PermissionApiService } from "@core/api/services/permission-api.service";

// Generated OpenAPI Services - login/register in UserApiService, other auth in AuthApiService
import { UserApiService as GeneratedUserService } from "@api/api/user.service";
import { AuthApiService as GeneratedAuthService } from "@api/api/auth.service";
import {
  LoginRequest,
  AuthResponse as ApiAuthResponse,
  RegisterRequest,
  RefreshRequest,
  ChangePasswordRequest as GeneratedChangePasswordRequest,
  ResetPasswordRequest as GeneratedResetPasswordRequest,
  ConfirmResetRequest,
  UserInfo,
  LogoutRequest,
} from "@api/model/models";

/**
 * Backend user object structure
 */
interface BackendUser {
  id?: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  permissions?: string[];
  isActive?: boolean;
  tenantId?: string | null;
  lastLogin?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Extended AuthResponse interface to handle backend field name differences.
 * Backend returns 'token' instead of 'accessToken' in some cases.
 * @see MEDIUM-6 code review fix
 */
interface BackendAuthResponse {
  /** JWT access token */
  token: string;
  /** Token expiration time in seconds */
  expiresIn: number;
  /** JWT refresh token for session persistence */
  refreshToken?: string | null;
  /** CSRF token for form submissions */
  csrfToken?: string;
  /** User info from the generated API */
  user: UserInfo | BackendUser;
}

export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  permissions: string[];
  isActive: boolean;
  lastLogin?: Date;
  tenantId: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
  /**
   * TOTP / backup code supplied when completing an MFA-gated login.
   *
   * GAP-004: The backend login endpoint enforces MFA inline — when an account
   * has MFA enabled and no code is provided it returns 401 "MFA code required".
   * The caller resubmits the same credentials plus this code to complete the
   * challenge and receive tokens. (MFA is NOT completed via /auth/mfa/verify,
   * which is session-protected and only serves already-authenticated step-up.)
   */
  mfaCode?: string;
  /**
   * Opaque WebAuthn login ceremony completion. The browser credential is sent
   * only to the login endpoint and is never persisted client-side.
   */
  securityKey?: SecurityKeyLoginCompletion;
}

export interface SecurityKeyLoginCompletion {
  ceremonyId: string;
  credential: object;
}

export interface RegisterCredentials {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken?: string;
  expiresIn: number;
  csrfToken?: string;
}

/**
 * Cross-tab logout signalling is best-effort. Cap the wait so a hung
 * BroadcastChannel/WebCrypto promise can never stall the logout redirect.
 */
const CROSS_TAB_LOGOUT_TIMEOUT_MS = 2_000;

/**
 * Secure Authentication Service with HttpOnly Cookie Support
 *
 * Security Features (DEV-1434 fixes):
 * - Access tokens stored in memory only (not localStorage)
 * - Refresh tokens handled ONLY via HttpOnly cookies (no localStorage - CWE-922 fix)
 * - CSRF protection with X-CSRF-TOKEN header
 * - All HTTP requests include withCredentials: true for cookie support
 * - Session indicator in localStorage (not sensitive data)
 * - Automatic token refresh on app initialization if session exists
 *
 * @see https://owasp.org/www-community/vulnerabilities/Insufficient_Session-ID_Length
 */
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly SESSION_KEY = "razor_session_active";
  private readonly USER_KEY = "razor_user";
  private readonly CSRF_HEADER = "X-CSRF-TOKEN";
  // SECURITY NOTE (DEV-1434): Refresh tokens are now handled exclusively via HttpOnly cookies
  // set by the backend. Access tokens are stored in memory only. No sensitive tokens are stored
  // in localStorage, which protects against XSS attacks (CWE-922).

  // In-memory token storage (security: tokens never persisted to localStorage)
  // Access token: stored in memory only, cleared on page refresh/close
  // Refresh token: handled exclusively via HttpOnly cookie (set by backend)
  private accessToken: string | null = null;
  private csrfToken: string | null = null;

  // COLD-LOAD-RACE FIX: single-flight refresh coordination.
  // On a cold load only the HttpOnly refresh cookie exists; the in-memory
  // access token is null and must be minted via /auth/refresh. Without
  // coordination, every early authenticated call (/tenants, /permissions,
  // /roles, effective-permissions) independently fires its own /auth/refresh
  // and/or races the refresh and 401s. We hold a single shared in-flight
  // refresh observable here so that concurrent callers await the SAME
  // refresh (single-flight) and queue behind it. Reset to null on
  // completion/error so the next genuine refresh starts fresh.
  private refreshInFlight$: Observable<string> | null = null;
  // Login, logout and explicit tenant changes invalidate asynchronous refresh work,
  // even when the same user signs in again with a different session.
  private sessionGeneration = 0;
  private readonly sessionChanges = new Subject<void>();

  private advanceSessionGeneration(): number {
    this.refreshInFlight$ = null;
    const generation = ++this.sessionGeneration;
    // This reaches the HttpClient subscription inside the Web-Lock promise,
    // so shareReplay cannot keep an obsolete refresh request alive.
    this.sessionChanges.next();
    return generation;
  }

  // Session-termination latch (incident 2026-06-30 — logout/refresh storm).
  // Set the instant a logout begins, cleared only on a fresh login
  // (setActiveSession(true)). It makes logout() idempotent/terminal and stops
  // the reactive refresh path from re-invoking handleLogout() once per request.
  // Without it, after the access token lapsed EVERY background poll that 401'd
  // re-entered refreshToken()→(!hasActiveSession)→handleLogout(), so the auth +
  // error interceptors and dozens of polls produced a multi-minute storm of
  // /auth/logout calls instead of a single clean redirect.
  //
  // NOTE: this is deliberately MINIMAL — it does NOT refactor the shared refresh
  // core. logout() stays inside refreshToken()/refreshTokenSingleFlight() so the
  // interceptor's pre-send ensureAccessToken() path keeps its logout+redirect on
  // a failed refresh (regression from a prior over-refactor, reverted).
  private sessionTerminated = false;

  // Cross-tab refresh serialization (incident 2026-06-30 root cause). The
  // backend rotates the refresh cookie on EVERY /auth/refresh, so two
  // concurrent refreshes race → the loser presents a rotated cookie → 401 →
  // session-family revocation → logout. A cross-tab Web Lock serialises ALL
  // refreshes so there is never a concurrent pair. The bounded timeout below
  // lives INSIDE refreshToken() (before its catchError) so a hung refresh both
  // (a) routes through handleLogout()+redirect and (b) cancels the XHR, which
  // releases the lock — without it an exclusive lock held across a no-timeout
  // XHR would wedge every tab's refresh permanently.
  private readonly TOKEN_REFRESH_LOCK = "razor_token_refresh";
  /** Max time one refresh may take (and hold the cross-tab lock) before it
   *  fails → logout. Generous so a slow-but-alive network is not a false logout. */
  private readonly REFRESH_TIMEOUT_MS = 10_000;

  // Private writable state
  private readonly _isAuthenticated = signal(false);
  private readonly _currentUser = signal<User | null>(null);
  private readonly _isLoading = signal(false);

  // COLD-LOAD GUARD-RACE FIX: "are currentUser().roles/.permissions currently
  // trustworthy?" gate. The route guards (roleGuard/permissionGuard/tenantGuard)
  // evaluate role/permission requirements SYNCHRONOUSLY against
  // currentUser().roles, but those are hydrated ASYNCHRONOUSLY by
  // loadEffectivePermissions(). On a cold load, route activation races that
  // hydration, so a privileged user can be denied (-> /unauthorized) purely on
  // timing. _authReady is:
  //   - false initially (no roles loaded yet)
  //   - set false at the START of every loadEffectivePermissions() (the window
  //     during which roles are being re-fetched / may be cleared — e.g. tenant
  //     switch), so a guard that wakes mid-reload waits rather than reading a
  //     transient empty set
  //   - set true at the END of loadEffectivePermissions() (success OR fail-safe;
  //     both are deterministic terminal states so guards never hang forever)
  //   - reset false on logout
  // Guards AWAIT this becoming true (with a finite timeout) before evaluating.
  private readonly _authReady = signal(false);

  // Public read-only signal access
  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly currentUser = this._currentUser.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly authReady = this._authReady.asReadonly();

  // Backward-compatible observables
  public readonly currentUser$ = toObservable(this.currentUser);
  public readonly isAuthenticated$ = toObservable(this.isAuthenticated);
  public readonly authReady$ = toObservable(this.authReady);

  // Single-flight guard for effective-permissions hydration. Concurrent callers
  // (APP_INITIALIZER initializeSession, establishTenantContext, switchTenant,
  // login/register store flows) share ONE in-flight fetch instead of each firing
  // their own /users/{id}/effective-permissions request. Keyed by both user and
  // selected tenant so a tenant switch cannot join or apply an older tenant's
  // request. A generation prevents a superseded request from reopening the
  // authReady gate while the current tenant is still hydrating.
  private effectivePermissionsInFlight: Promise<boolean> | null = null;
  private effectivePermissionsInFlightKey: string | null = null;
  private effectivePermissionsGeneration = 0;

  // DEV-2041: Logout deduplication flag to prevent multiple concurrent logout calls
  // This prevents 50+ duplicate POST /api/v1/auth/logout requests when session ends
  private isLoggingOut = false;

  private readonly logger = inject(LoggerService);
  private readonly injector = inject(Injector);
  private readonly sharedAuthState = inject(SharedAuthStateService);
  private readonly sentryService = inject(SentryService);
  private readonly appStorage = inject(AppStorageService);

  // Lazy-loaded to avoid circular dependency
  private _crossTabAuthService: CrossTabAuthService | null = null;
  // Lazy-loaded to avoid a DI cycle: PermissionApiService -> generated client ->
  // HttpClient is acyclic, but we keep parity with crossTabAuthService and only
  // construct it on first use (it is not needed for the unauthenticated layout).
  private _permissionApiService: PermissionApiService | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private userMgmtApi: GeneratedUserService,
    private authApi: GeneratedAuthService,
  ) {
    this.initializeAuthState();
  }

  /**
   * Get CrossTabAuthService lazily to avoid circular dependency
   */
  private get crossTabAuthService(): CrossTabAuthService | null {
    if (!this._crossTabAuthService) {
      try {
        // Lazy import to avoid circular dependency
        this._crossTabAuthService = this.injector.get(CrossTabAuthService);
      } catch {
        // Service not available yet
        return null;
      }
    }
    return this._crossTabAuthService;
  }

  /**
   * Get PermissionApiService lazily. Resolved on first use so the
   * unauthenticated auth layout never constructs the RBAC client.
   */
  private get permissionApiService(): PermissionApiService | null {
    if (!this._permissionApiService) {
      try {
        this._permissionApiService = this.injector.get(PermissionApiService);
      } catch {
        return null;
      }
    }
    return this._permissionApiService;
  }

  /**
   * Fetch the current user's effective roles + permissions from the backend
   * and merge their NAME strings into the in-memory currentUser signal.
   *
   * CRITICAL (auth-wiring fix): The login/refresh responses do NOT reliably
   * carry the user's roles/permissions, and session restore rehydrates only a
   * minimal {id, tenantId} skeleton. Route guards (roleGuard/permissionGuard)
   * read currentUser().roles / .permissions via AuthStore, so without this they
   * evaluate against EMPTY arrays and deny a fully-privileged superuser every
   * role/permission-gated route.
   *
   * Source of truth: GET /api/v1/users/{id}/effective-permissions, which returns
   * BOTH roles[].name and permissions[].name — a single round-trip for both.
   *
   * Returns true on success (user populated), false on failure. Fails SAFE:
   * the previous authority is cleared before fetching, so a failed tenant
   * switch or reload cannot retain permissions from an older snapshot. It
   * never throws, so it cannot wedge app bootstrap or leave an infinite
   * spinner.
   */
  async loadEffectivePermissions(selectedTenantId?: string): Promise<boolean> {
    const user = this._currentUser();
    if (!user?.id) {
      this.logger.debug(
        "[AuthService] loadEffectivePermissions skipped: no current user",
      );
      // No principal -> roles are trivially "settled" (empty). Mark ready so an
      // unauthenticated guard tick is never left waiting on a load that will
      // never run; the guards still short-circuit unauthenticated users to login
      // before they ever consult authReady.
      this._authReady.set(true);
      return false;
    }

    // SINGLE-FLIGHT: if a hydration for THIS principal is already running, share
    // it. This dedupes the concurrent cold-load callers (initializeSession +
    // establishTenantContext + switchTenant) that previously each fired their own
    // effective-permissions request and each toggled _authReady, creating extra
    // empty-roles windows. A different principal (id changed) bypasses the shared
    // promise so we never serve stale hydration.
    const tenantId = selectedTenantId ?? user.tenantId;
    if (!tenantId) {
      // A tenantless identity is not implicitly attached to a made-up tenant.
      // Invalidate any older tenant hydration and leave authority empty until
      // an explicit tenant selection establishes a real scope.
      this.effectivePermissionsGeneration++;
      this.effectivePermissionsInFlight = null;
      this.effectivePermissionsInFlightKey = null;
      const authorityOwner = this._currentUser();
      if (authorityOwner?.id === user.id) {
        if (authorityOwner.tenantId !== null) {
          this.advanceSessionGeneration();
        }
        this.setCurrentUser({
          ...authorityOwner,
          tenantId: null,
          roles: [],
          permissions: [],
        });
      }
      this._authReady.set(true);
      this.logger.warn(
        "[AuthService] Effective permissions require an explicit tenant context",
        { userId: user.id },
      );
      return false;
    }
    const hydrationKey = `${user.id}:${tenantId}`;
    if (
      this.effectivePermissionsInFlight &&
      this.effectivePermissionsInFlightKey === hydrationKey
    ) {
      this.logger.debug(
        "[AuthService] loadEffectivePermissions joining in-flight hydration",
        { userId: user.id, tenantId },
      );
      return this.effectivePermissionsInFlight;
    }

    // Entering the (re)hydration window: roles/permissions are about to be
    // re-fetched and must be treated as NOT-yet-trustworthy until this settles.
    // This is what makes a tenant-switch reload safe — switchTenant() calls this
    // method, so the gate covers that window automatically.
    this._authReady.set(false);
    const authorityOwner = this._currentUser();
    if (authorityOwner?.id === user.id) {
      if (authorityOwner.tenantId !== tenantId) {
        this.advanceSessionGeneration();
      }
      this.setCurrentUser({
        ...authorityOwner,
        tenantId,
        roles: [],
        permissions: [],
      });
    }

    const generation = ++this.effectivePermissionsGeneration;

    const inFlight = this.loadEffectivePermissionsInternal(
      user.id,
      tenantId,
      generation,
    ).finally(() => {
      if (generation !== this.effectivePermissionsGeneration) {
        return;
      }
      // Settle: roles/permissions are now trustworthy (whether the fetch
      // succeeded or failed-safe). Clear the in-flight slot so the next genuine
      // reload starts fresh, and open the gate so waiting guards proceed.
      this.effectivePermissionsInFlight = null;
      this.effectivePermissionsInFlightKey = null;
      this._authReady.set(true);
    });

    this.effectivePermissionsInFlight = inFlight;
    this.effectivePermissionsInFlightKey = hydrationKey;
    return inFlight;
  }

  /**
   * Internal worker for {@link loadEffectivePermissions}. Performs the actual
   * GET /users/{id}/effective-permissions round-trip and patches currentUser.
   * Wrapped by the public method which owns single-flight + the authReady gate.
   * Fails SAFE: never throws, returns false on any error.
   */
  private async loadEffectivePermissionsInternal(
    userId: string,
    tenantId: string,
    generation: number,
  ): Promise<boolean> {
    const user = this._currentUser();
    if (!user?.id || user.id !== userId || user.tenantId !== tenantId) {
      this.logger.debug(
        "[AuthService] loadEffectivePermissions skipped: principal changed before load",
      );
      return false;
    }

    const permissionApi = this.permissionApiService;
    if (!permissionApi) {
      this.logger.warn(
        "[AuthService] PermissionApiService unavailable; cannot load effective permissions",
      );
      return false;
    }

    try {
      const effective = await firstValueFrom(
        permissionApi.getEffectivePermissions(user.id),
      );

      // Map to NAME strings — the contract guards compare against (e.g.
      // 'system-admin', 'users:read'). Dedupe defensively.
      const roleNames = [
        ...new Set((effective.roles ?? []).map((r) => r.name).filter(Boolean)),
      ];
      const permissionNames = [
        ...new Set(
          (effective.permissions ?? []).map((p) => p.name).filter(Boolean),
        ),
      ];

      // Re-read the signal in case it changed (e.g. logout raced); only patch a
      // live user with the same id to avoid clobbering a newer principal.
      const latest = this._currentUser();
      if (
        !latest ||
        latest.id !== user.id ||
        latest.tenantId !== tenantId ||
        generation !== this.effectivePermissionsGeneration
      ) {
        this.logger.debug(
          "[AuthService] currentUser changed during effective-permissions load; skipping patch",
        );
        return false;
      }

      const updated: User = {
        ...latest,
        tenantId,
        roles: roleNames,
        permissions: permissionNames,
      };
      this.setCurrentUser(updated);

      this.logger.debug(
        "[AuthService] Effective roles/permissions applied to currentUser",
        {
          userId: user.id,
          roleCount: roleNames.length,
          permissionCount: permissionNames.length,
        },
      );
      return true;
    } catch (error) {
      // Fail SAFE: leave roles/permissions empty (guards deny) and surface the
      // error. Do NOT log the user out here — a transient RBAC fetch failure
      // should not destroy a valid session; the user simply sees /unauthorized
      // and can retry.
      this.logger.error("[AuthService] Failed to load effective permissions", {
        error,
      });
      return false;
    }
  }

  /**
   * Resolve once role/permission hydration for the active principal has settled
   * (authReady === true), or after `timeoutMs` as a fail-safe. Used by the route
   * guards to avoid evaluating role/permission requirements against a transient
   * empty role set during cold-load / tenant-switch hydration.
   *
   * - Resolves IMMEDIATELY (synchronously-ish, next microtask) when already ready
   *   — no regression for warm navigation.
   * - On timeout it resolves anyway (does not reject): the guard then evaluates
   *   against whatever roles exist and fails SAFE (deny) rather than hanging the
   *   navigation. The timeout is generous relative to the <100ms backend.
   *
   * Returns the readiness state observed at resolution time (true if the gate
   * opened, false if we resolved on timeout while still not ready).
   */
  async whenAuthReady(timeoutMs = 5000): Promise<boolean> {
    if (this._authReady()) {
      return true;
    }
    return firstValueFrom(
      race([
        this.authReady$.pipe(
          filter((ready) => ready === true),
          take(1),
        ),
        timer(timeoutMs).pipe(map(() => this._authReady())),
      ]),
    );
  }

  /**
   * Initialize authentication state from stored session data.
   * HIGH-1 FIX (DEV-1434): No longer reads refresh token from localStorage.
   * Refresh tokens are now handled exclusively via HttpOnly cookies.
   *
   * CRITICAL FIX: We now set isAuthenticated=true immediately if there's a stored session.
   * This allows router guards to pass during direct URL navigation. The actual token refresh
   * happens via APP_INITIALIZER (initializeSession), and if it fails, the user is logged out.
   *
   * Previous approach (setting isAuthenticated only after token refresh) caused a race condition:
   * 1. User navigates directly to /settings/profile
   * 2. APP_INITIALIZER starts initializeSession() but doesn't block
   * 3. Router guards run with isAuthenticated=false
   * 4. User gets redirected to login even though they have a valid session
   *
   * New approach:
   * - Set isAuthenticated=true immediately based on stored session
   * - Guards pass and allow navigation
   * - Token refresh happens in background via APP_INITIALIZER
   * - If refresh fails, handleLogout() is called and user is redirected to login
   * - HTTP requests without a valid token will trigger 401 -> refreshToken() -> retry
   */
  /**
   * Initialize authentication state from stored session indicator.
   *
   * SECURITY (DEV-1434): Access tokens are NOT persisted - they exist only in memory.
   * Refresh tokens are handled exclusively via HttpOnly cookies (set by backend).
   * This prevents XSS attacks from stealing tokens (CWE-922).
   *
   * On page load, if a session indicator exists, we set isAuthenticated=true
   * to allow router guards to pass. The APP_INITIALIZER then calls initializeSession()
   * which refreshes the access token using the HttpOnly cookie.
   */
  private initializeAuthState(): void {
    const hasActiveSession = this.hasActiveSession();
    const user = this.getStoredUser();

    if (hasActiveSession && user) {
      // Set user data AND isAuthenticated immediately
      // This allows router guards to pass during direct URL navigation
      // Access token will be obtained via token refresh in initializeSession()
      this.setCurrentUser(user);
      this._isAuthenticated.set(true);

      // Also update SharedAuthStateService for TenantStore and other consumers
      this.sharedAuthState.setState({
        isAuthenticated: true,
        userId: user.id,
        tenantId: user.tenantId,
      });

      this.logger.debug(
        "[AuthService] Restored auth state from stored session",
        {
          userId: user.id,
          email: user.email,
          note: "Access token will be refreshed via HttpOnly cookie",
        },
      );
    }
  }

  /**
   * Safely parse a date string, returning undefined for invalid values.
   * Prevents Invalid Date objects from being created when the input is
   * null, undefined, or an invalid date string.
   */
  private parseDate(value: string | undefined | null): Date | undefined {
    if (!value) {
      return undefined;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
  }

  // Type mappers: Convert between generated OpenAPI types and local types
  private mapApiUserToUser(userInfo: UserInfo): User {
    return {
      id: userInfo.id ?? "",
      username: userInfo.username ?? userInfo.email ?? "",
      email: userInfo.email ?? "",
      firstName: userInfo.firstName ?? "",
      lastName: userInfo.lastName ?? "",
      roles: userInfo.roles ?? [],
      permissions: userInfo.permissions ?? [],
      isActive: userInfo.isActive ?? true,
      lastLogin: userInfo.lastLogin ? new Date(userInfo.lastLogin) : undefined,
      tenantId: userInfo.tenantId ?? null,
      createdAt: userInfo.createdAt ? new Date(userInfo.createdAt) : new Date(),
      updatedAt: userInfo.updatedAt ? new Date(userInfo.updatedAt) : new Date(),
    };
  }

  /**
   * Maps the backend ApiAuthResponse to our internal AuthResponse format.
   * MEDIUM-6 FIX (DEV-1439): Uses BackendAuthResponse type instead of 'as any'
   * for type-safe access to backend-specific fields.
   */
  private mapApiAuthResponseToAuthResponse(
    response: ApiAuthResponse,
  ): AuthResponse {
    // ApiAuthResponse from generated API has: token, csrfToken, expiresIn, refreshToken, user (UserInfo)
    const rawResponse = response as BackendAuthResponse;
    const token = rawResponse.token ?? response.token ?? "";

    // Extract user from response - use the nested 'user' object
    const apiUser = response.user;
    const backendUser = rawResponse.user;
    const user: User = {
      id: apiUser?.id ?? backendUser?.id ?? "",
      username:
        apiUser?.username ?? backendUser?.username ?? apiUser?.email ?? "",
      email: apiUser?.email ?? backendUser?.email ?? "",
      firstName: apiUser?.firstName ?? backendUser?.firstName ?? "",
      lastName: apiUser?.lastName ?? backendUser?.lastName ?? "",
      roles: apiUser?.roles ?? backendUser?.roles ?? [],
      permissions: apiUser?.permissions ?? backendUser?.permissions ?? [],
      isActive: apiUser?.isActive ?? backendUser?.isActive ?? true,
      tenantId: apiUser?.tenantId ?? backendUser?.tenantId ?? null,
      lastLogin: this.parseDate(apiUser?.lastLogin ?? backendUser?.lastLogin),
      createdAt:
        this.parseDate(apiUser?.createdAt ?? backendUser?.createdAt) ??
        new Date(),
      updatedAt:
        this.parseDate(apiUser?.updatedAt ?? backendUser?.updatedAt) ??
        new Date(),
    };

    return {
      user,
      token,
      refreshToken: response.refreshToken ?? undefined,
      csrfToken: response.csrfToken ?? rawResponse.csrfToken,
      expiresIn: response.expiresIn ?? 3600,
    };
  }

  /**
   * Public method to refresh token, called by APP_INITIALIZER.
   *
   * This method runs during app bootstrap to refresh the access token using the
   * HttpOnly refresh token cookie. The auth state (isAuthenticated) is already
   * set by initializeAuthState() to allow guards to pass during direct URL navigation.
   *
   * If token refresh succeeds: access token is updated, user can make API requests
   * If token refresh fails: user is logged out and redirected to login
   */
  async initializeSession(): Promise<void> {
    if (this._isLoading()) {
      return;
    }
    const generation = this.sessionGeneration;
    const hasActiveSession = this.hasActiveSession();
    const user = this.getStoredUser();

    if (!hasActiveSession || !user) {
      this.logger.debug("[AuthService] No active session to initialize");
      return;
    }

    this.logger.debug(
      "[AuthService] Initializing session, refreshing access token...",
    );

    // Use firstValueFrom to convert Observable to Promise - automatically completes after first emission
    // This prevents memory leaks from unmanaged subscriptions (code review fix)
    //
    // COLD-LOAD-RACE FIX: route the bootstrap refresh through the SINGLE-FLIGHT
    // path, not refreshToken() directly. On a cold load this APP_INITIALIZER
    // refresh and the auth interceptor's ensureAccessToken() refresh (fired by
    // the first guarded request) would otherwise each call /auth/refresh
    // independently. Because the backend ROTATES the refresh-token cookie on
    // every use, the second of the two racing refreshes presents an
    // already-rotated token and 401s — corrupting session/token state
    // mid-bootstrap and cascading to a /tenants/switch 401 → logout.
    // Sharing one in-flight refresh eliminates the rotation race.
    try {
      const token = await firstValueFrom(this.refreshTokenSingleFlight());
      if (generation !== this.sessionGeneration) {
        return;
      }
      if (token) {
        this.logger.debug(
          "[AuthService] Access token refreshed successfully on app initialization",
        );
        // isAuthenticated is already set by initializeAuthState(), no need to set again
        // Just ensure shared state is up-to-date
        this.sharedAuthState.setState({
          isAuthenticated: true,
          userId: user.id,
          tenantId: user.tenantId,
        });

        // CRITICAL (auth-wiring fix): hydrate roles/permissions BEFORE this
        // blocking APP_INITIALIZER resolves. Session restore rehydrates only a
        // {id, tenantId} skeleton, so without this the route guards would
        // evaluate against empty roles/permissions during the first navigation
        // and deny a privileged user. Awaiting here guarantees the data is in
        // currentUser() before any protected route activates — no guard race.
        await this.loadEffectivePermissions();
      } else {
        this.logger.warn("[AuthService] Token refresh returned empty token");
      }
    } catch (error) {
      if (generation !== this.sessionGeneration) {
        return;
      }
      this.logger.warn(
        "[AuthService] Failed to refresh token on app initialization:",
        { error },
      );
      // Clear auth state since token refresh failed - user will be redirected to login
      await this.handleLogout();
    }
  }

  login(credentials: LoginCredentials): Observable<AuthResponse> {
    const generation = this.advanceSessionGeneration();
    if (generation !== this.sessionGeneration) {
      return EMPTY;
    }
    this.isLoggingOut = false;
    this._isLoading.set(true);

    // LoginRequest expects 'username' which can be either email or username
    const loginRequest: LoginRequest = {
      username: credentials.email, // Backend accepts email in the username field
      password: credentials.password,
      // Pass rememberMe to backend so it can set persistent vs session cookie
      // - rememberMe=true: 7-day persistent cookie (survives browser restart)
      // - rememberMe=false: session cookie (expires when browser closes)
      rememberMe: credentials.rememberMe ?? false,
      // GAP-004: Forward the MFA code (if any) so an MFA-gated account can
      // complete login in a single round-trip after the challenge is shown.
      // Omit the field entirely when no code is present so the backend issues
      // the 401 "MFA code required" challenge on the first attempt.
      ...(credentials.mfaCode ? { mfaCode: credentials.mfaCode } : {}),
      ...(credentials.securityKey
        ? { securityKey: credentials.securityKey }
        : {}),
    };

    const loginRequest$ = credentials.securityKey
      ? this.userMgmtApi.finishSecurityKeyLogin(loginRequest)
      : this.userMgmtApi.login(loginRequest);

    return defer(() =>
      generation === this.sessionGeneration ? loginRequest$ : EMPTY,
    ).pipe(
      takeUntil(this.sessionChanges),
      filter(() => generation === this.sessionGeneration),
      // HIGH-1 FIX (DEV-1434): Removed localStorage storage of refresh token.
      // Refresh token is now handled exclusively via HttpOnly cookie set by backend.
      // This prevents XSS attacks from stealing the refresh token (CWE-922).
      map((response: ApiAuthResponse) =>
        this.mapApiAuthResponseToAuthResponse(response),
      ),
      tap((authResponse: AuthResponse) => {
        this.handleAuthSuccess(authResponse, generation);
      }),
      catchError((error) => {
        if (generation !== this.sessionGeneration) {
          return EMPTY;
        }
        this._isLoading.set(false);
        return throwError(() => error);
      }),
    );
  }

  register(credentials: RegisterCredentials): Observable<AuthResponse> {
    const generation = this.advanceSessionGeneration();
    if (generation !== this.sessionGeneration) {
      return EMPTY;
    }
    this._isLoading.set(true);
    let registrationAccepted = false;

    const registerRequest: RegisterRequest = {
      email: credentials.email,
      password: credentials.password,
      username: credentials.username,
    };

    return defer(() =>
      generation === this.sessionGeneration
        ? this.userMgmtApi.register(registerRequest)
        : EMPTY,
    ).pipe(
      // Once the response is accepted, auto-login establishes its own epoch.
      // Its synchronous transition must not complete switchMap before its
      // inner login subscription exists. Pending registration stays cancellable.
      takeUntil(this.sessionChanges.pipe(filter(() => !registrationAccepted))),
      filter(() => generation === this.sessionGeneration),
      catchError((error) => {
        if (generation !== this.sessionGeneration) {
          return EMPTY;
        }
        this._isLoading.set(false);
        return throwError(() => error);
      }),
      tap(() => {
        registrationAccepted = true;
      }),
      // Registration returns a generic response which doesn't include tokens
      // After registration, we need to login to get tokens
      switchMap(() =>
        // Auto-login after registration
        this.login({
          email: credentials.email,
          password: credentials.password,
        }),
      ),
    );
  }

  logout(): void {
    // Terminal & idempotent (incident 2026-06-30): once a logout is underway the
    // session is dead until the next login. This collapses the storm where the
    // auth interceptor, the error interceptor, and every 401'd background poll
    // each call logout() in turn. The latch resets on a fresh login
    // (setActiveSession(true)), so a subsequent session can still log out.
    if (this.sessionTerminated && !this._isLoading()) {
      this.logger.debug(
        "[AuthService] Logout already terminal — ignoring duplicate call",
      );
      return;
    }
    this.sessionTerminated = true;
    this._isLoading.set(false);
    const generation = this.advanceSessionGeneration();
    if (generation !== this.sessionGeneration) {
      return;
    }

    // DEV-2041: Prevent multiple concurrent logout calls
    // When session ends, multiple 401 errors can trigger simultaneous logout attempts
    // This deduplication ensures only one POST /api/v1/auth/logout request is made
    if (this.isLoggingOut) {
      this.logger.debug(
        "[AuthService] Logout already in progress, skipping duplicate call",
      );
      return;
    }
    this.isLoggingOut = true;

    // Use take(1) to ensure automatic completion after first emission
    // This prevents memory leaks from unmanaged subscriptions (code review fix)
    const logoutRequest: LogoutRequest = { everywhere: false };
    this.authApi
      .logout(logoutRequest)
      .pipe(take(1), takeUntil(this.sessionChanges))
      .subscribe({
        next: () => {
          if (generation === this.sessionGeneration) {
            void this.handleLogout();
          }
        },
        error: () => {
          if (generation === this.sessionGeneration) {
            void this.handleLogout();
          }
        },
      });
  }

  /**
   * Refresh the access token using HttpOnly cookie.
   *
   * SECURITY (DEV-1434): The refresh token is sent automatically via HttpOnly cookie
   * (set by backend with credentials: 'include'). This prevents XSS attacks from
   * stealing the refresh token since JavaScript cannot access HttpOnly cookies.
   *
   * The backend supports a body fallback for cross-origin scenarios, but we prefer
   * the cookie method for security. If the response includes a new refresh token,
   * it's for cross-origin fallback only - the secure path uses cookies exclusively.
   *
   * MEDIUM-6 FIX (DEV-1439): Uses BackendAuthResponse type instead of 'as any'.
   */
  refreshToken(): Observable<string> {
    const generation = this.sessionGeneration;
    return defer(() => {
      if (generation !== this.sessionGeneration || this._isLoading()) {
        return EMPTY;
      }
      if (this.isSessionInactive()) {
        // Session already ended (or ending). Terminate EXACTLY ONCE — do not
        // re-invoke logout per call. The first logout already cleared state and
        // redirected; re-calling handleLogout() for every queued poll request is
        // precisely what produced the logout/refresh storm (incident 2026-06-30).
        if (!this.sessionTerminated) {
          void this.handleLogout();
        }
        return of("");
      }

      // Empty request body - refresh token is sent via HttpOnly cookie automatically
      // The backend will read the 'refresh_token' cookie set during login
      const request: RefreshRequest = { refreshToken: "" };

      return this.authApi.refreshToken(request).pipe(
        filter(() => generation === this.sessionGeneration),
        tap((response: ApiAuthResponse) => {
          // Backend returns 'token' field
          // MEDIUM-6 FIX: Use BackendAuthResponse type for type safety
          const rawResponse = response as BackendAuthResponse;
          const token = rawResponse.token ?? response.token ?? null;

          // Store access token in memory only (not localStorage - security)
          this.accessToken = token;

          // SEC-001: Rehydrate full user data from refresh response.
          // getStoredUser() returns a skeleton with empty roles/permissions;
          // this populates the real user after token refresh.
          const authResponse = this.mapApiAuthResponseToAuthResponse(response);
          if (authResponse.user?.id) {
            // Empty arrays are authoritative: the backend refresh response is
            // projected from the same live tenant-scoped graph as middleware.
            // Never carry old roles/permissions across a refresh.
            this.setCurrentUser(authResponse.user);
            this.storeUser(authResponse.user);
          }

          // SE-02: Update CSRF token from refresh response to prevent stale token drift.
          // Backend generates a new csrf_token on each refresh; if we don't store it,
          // the in-memory token diverges from the HttpOnly cookie, causing validation failures.
          if (authResponse.csrfToken) {
            this.csrfToken = authResponse.csrfToken;
          }

          // Note: New refresh token is set automatically via HttpOnly cookie by backend
          // We don't need to store it - the browser handles it securely

          // Broadcast token refresh to other tabs (DEV-406)
          if (token) {
            this.crossTabAuthService?.broadcastTokenRefresh();
          }
        }),
        map((response: ApiAuthResponse) => {
          const rawResponse = response as BackendAuthResponse;
          return rawResponse.token ?? response.token ?? "";
        }),
        // Bound the round-trip. MUST be UPSTREAM of the catchError below so a
        // TimeoutError routes through handleLogout()+redirect (the pre-send
        // interceptor path has no catchError of its own), AND so the timeout
        // unsubscribes/cancels the XHR — which is what releases the cross-tab lock
        // held across this call in refreshTokenCrossTabLocked().
        timeout({ first: this.REFRESH_TIMEOUT_MS }),
        takeUntil(this.sessionChanges),
        catchError((error) => {
          if (generation !== this.sessionGeneration) {
            return EMPTY;
          }
          void this.handleLogout();
          return throwError(() => error);
        }),
      );
    });
  }

  /**
   * Cross-tab-serialised refresh. Wraps {@link refreshToken} in a Web Lock so
   * only ONE tab refreshes at a time across the whole origin — eliminating the
   * concurrent-refresh cookie-rotation race that 401s the loser and revokes the
   * token family (incident 2026-06-30; confirmed by a live /auth/refresh trace:
   * a SINGLE refresh always succeeds, only concurrent pairs race).
   *
   * Safety invariants (per security + auth design review):
   * - logout stays INSIDE refreshToken() for EVERY failure mode (HTTP error and
   *   timeout), so the no-catchError interceptor pre-send path still redirects.
   * - the lock is held for exactly one refresh round-trip; the inner timeout()
   *   guarantees firstValueFrom() always settles, so the lock ALWAYS releases
   *   (no app-wide refresh deadlock). The spec also auto-releases on tab close.
   * - if the lock can't be ACQUIRED for ANY reason (callback never ran — bfcache
   *   InvalidStateError, opaque-origin SecurityError, an AbortError, or even a
   *   synchronous throw from request()), DEGRADE to an unlocked in-tab refresh
   *   (which still owns logout) rather than letting the rejection reach the
   *   no-catchError pre-send path un-redirected. We discriminate on whether the
   *   critical section actually ran (a closure flag), NOT on the error name —
   *   a name-allowlist strands every unrecognised acquisition failure, and
   *   degrading a failure that DID run would fire a second /auth/refresh and
   *   re-introduce the very cookie-rotation race this serialisation kills.
   * - the whole thing is wrapped in defer() so the lock + refresh fire on
   *   SUBSCRIPTION (cold), never eagerly at construction — a future caller that
   *   builds-but-discards this observable must not rotate the refresh cookie.
   * - no access token is ever broadcast across tabs (stays in-memory only,
   *   CWE-922); sibling tabs simply queue and re-refresh sequentially.
   */
  private refreshTokenCrossTabLocked(): Observable<string> {
    const generation = this.sessionGeneration;
    // Capture ownership before waiting for the cross-tab lock. An obsolete
    // queued caller must never rotate the replacement session's cookie.
    const refresh = (): Observable<string> =>
      generation === this.sessionGeneration ? this.refreshToken() : EMPTY;
    const locks =
      typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (!locks?.request) {
      // SSR / browsers without Web Locks → today's in-tab single-flight only.
      return refresh();
    }
    return defer(() => {
      // True once the lock's critical section actually begins executing. This is
      // the ONLY reliable signal of "did our refresh run?" — error names are not.
      let criticalSectionRan = false;
      let lockPromise: Promise<string>;
      try {
        lockPromise = locks.request(this.TOKEN_REFRESH_LOCK, () => {
          criticalSectionRan = true;
          return firstValueFrom(refresh());
        });
      } catch {
        // request() threw synchronously (host/programming error) — the callback
        // never ran. Degrade to an unlocked refresh (logout owned downstream).
        return refresh();
      }
      return from(
        lockPromise.catch((err: unknown) => {
          if (!criticalSectionRan) {
            // Lock was never acquired (any reason) → refreshToken() was never
            // subscribed → no logout has happened. Fall back to an unlocked
            // refresh, which owns logout for every failure mode.
            return firstValueFrom(refresh());
          }
          // The critical section ran and refreshToken() failed: its catchError
          // already invoked handleLogout(). Re-propagate WITHOUT re-refreshing
          // (a second refresh here would re-race the rotated cookie).
          throw err;
        }),
      ).pipe(
        catchError((error: unknown) =>
          error instanceof EmptyError && generation !== this.sessionGeneration
            ? EMPTY
            : throwError(() => error),
        ),
      );
    });
  }

  /**
   * Single-flight token refresh.
   *
   * COLD-LOAD-RACE FIX: Concurrent callers (the auth interceptor handling
   * several early authenticated requests at once on a cold load) all await
   * the SAME in-flight refresh instead of each firing their own
   * /auth/refresh. The first caller starts the refresh; subsequent callers
   * subscribe to the shared, replayed result while it is in flight. Once it
   * settles (success or error) the shared handle is cleared so the next
   * genuine refresh starts cleanly.
   *
   * Use this (not refreshToken()) from the interceptor's queue-before-send
   * and 401-retry paths to avoid N parallel /auth/refresh calls.
   */
  refreshTokenSingleFlight(): Observable<string> {
    if (this._isLoading()) {
      return EMPTY;
    }
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    // Cross-tab serialised (one lock request per in-tab cycle — the shareReplay
    // below keeps concurrent in-tab callers on one refresh, hence one lock).
    const inFlight = this.refreshTokenCrossTabLocked().pipe(
      // Clear the shared handle once settled so the next refresh is fresh.
      finalize(() => {
        if (this.refreshInFlight$ === inFlight) {
          this.refreshInFlight$ = null;
        }
      }),
      // Replay the single result/error to every queued subscriber.
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.refreshInFlight$ = inFlight;
    return inFlight;
  }

  /**
   * Returns a valid (present, unexpired) access token, refreshing first if
   * necessary via the single-flight refresh.
   *
   * COLD-LOAD-RACE FIX: On a cold load this.accessToken is null until the
   * refresh cookie has been exchanged for an access token. The interceptor
   * calls this BEFORE sending an authenticated request so the request waits
   * for (and shares) one refresh rather than firing token-less and 401ing.
   *
   * - If a valid token already exists, emits it immediately (no network).
   * - If there is no active session, emits '' (caller sends without a Bearer
   *   token; the backend challenge / existing guard flow handles it).
   * - Otherwise performs a single-flight refresh and emits the fresh token.
   */
  ensureAccessToken(): Observable<string> {
    if (this._isLoading()) {
      return EMPTY;
    }
    if (this.isSessionInactive()) {
      // No live session to refresh from — don't re-enter the terminated-session
      // logout path. refreshTokenSingleFlight() (below) still owns logout+redirect
      // on a genuine refresh FAILURE for an active session.
      return of("");
    }
    if (this.accessToken && !this.isTokenExpired()) {
      return of(this.accessToken);
    }
    return this.refreshTokenSingleFlight();
  }

  resetPassword(email: string): Observable<void> {
    const request: GeneratedResetPasswordRequest = { email };
    return this.authApi.resetPassword(request).pipe(map(() => undefined));
  }

  confirmPasswordReset(token: string, newPassword: string): Observable<void> {
    const request: ConfirmResetRequest = { token, new_password: newPassword };
    return this.authApi
      .confirmResetPassword(request)
      .pipe(map(() => undefined));
  }

  changePassword(
    currentPassword: string,
    newPassword: string,
  ): Observable<void> {
    const request: GeneratedChangePasswordRequest = {
      current_password: currentPassword,
      new_password: newPassword,
    };
    return this.authApi.changePassword(request).pipe(map(() => undefined));
  }

  hasRole(role: string): boolean {
    const user = this.currentUser();
    if (!user) {
      return false;
    }
    // Case-insensitive to match the backend contract: the Rust role checks use
    // `eq_ignore_ascii_case` (e.g. roles_have_any / is_admin_websocket_user), so
    // a role claim of 'Admin' must satisfy hasRole('admin') as it does server-side.
    const target = role.toLowerCase();
    return user.roles.some((r) => r.toLowerCase() === target);
  }

  hasPermission(permission: string): boolean {
    const user = this.currentUser();
    return user?.permissions.includes(permission) || false;
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  /**
   * Handle successful authentication.
   *
   * SECURITY (DEV-1434): Access token is stored in memory only (not localStorage).
   * Refresh token is handled via HttpOnly cookie set by the backend - we don't
   * touch it here to prevent XSS attacks (CWE-922).
   */
  private handleAuthSuccess(
    response: AuthResponse,
    ownerGeneration?: number,
  ): void {
    // Login already owns this epoch; advancing it here would cancel its own
    // success emission. Other activation paths still establish a new epoch.
    const generation = ownerGeneration ?? this.advanceSessionGeneration();
    if (generation !== this.sessionGeneration) {
      return;
    }
    // Store access token in memory only (not localStorage - security)
    this.accessToken = response.token;

    // Note: Refresh token is set as HttpOnly cookie by backend
    // We don't store it in JavaScript to prevent XSS attacks

    if (response.csrfToken) {
      this.csrfToken = response.csrfToken;
    }

    this.storeUser(response.user);
    this.setActiveSession(true);

    this.setCurrentUser(response.user);
    this._isAuthenticated.set(true);
    this._isLoading.set(false);

    // ARCH-002: Update SharedAuthStateService for TenantStore and other consumers
    this.sharedAuthState.setState({
      isAuthenticated: true,
      userId: response.user.id,
      tenantId: response.user.tenantId,
    });

    // Broadcast login to other tabs (DEV-406)
    this.crossTabAuthService?.broadcastLogin({
      id: response.user.id,
      username: response.user.username,
      firstName: response.user.firstName,
      lastName: response.user.lastName,
      roles: response.user.roles,
    });

    this.logger.debug(
      "[AuthService] Auth success - access token in memory, refresh token in HttpOnly cookie",
    );
  }

  private async handleLogout(): Promise<void> {
    const generation = this.advanceSessionGeneration();
    if (generation !== this.sessionGeneration) {
      return;
    }
    this._isLoading.set(false);
    this.isLoggingOut = false;
    // Latch the session as terminated for the direct-teardown paths that reach
    // handleLogout() without going through logout() (cold-init failure, the
    // refresh fail-safe). Idempotent with logout()'s own set.
    this.sessionTerminated = true;

    // Broadcast logout to other tabs before clearing state (DEV-406).
    // BEST EFFORT: broadcastLogout() awaits WebCrypto HMAC sign + key rotation.
    // If any of that rejects (crypto unavailable, storage blocked, a
    // BroadcastChannel error), the rejection used to abort handleLogout()
    // *before* the redirect below — the server session was already destroyed,
    // yet the browser stayed on the authenticated shell, still polling APIs
    // that now 401. Caught by the production live smoke 2026-07-27. Cross-tab
    // signalling is an optimisation; it must never block local logout.
    // Guarded against BOTH failure modes: a rejection *and* a promise that
    // never settles (the observed production symptom had no error at all).
    // The timer is always cleared so the normal path leaves nothing pending.
    let broadcastTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        this.crossTabAuthService?.broadcastLogout() ?? Promise.resolve(),
        new Promise<void>((resolve) => {
          broadcastTimer = setTimeout(resolve, CROSS_TAB_LOGOUT_TIMEOUT_MS);
        }),
      ]);
    } catch (error) {
      this.logger.warn(
        "[AuthService] Cross-tab logout broadcast failed; continuing with local logout",
        { error },
      );
    } finally {
      if (broadcastTimer !== undefined) {
        clearTimeout(broadcastTimer);
      }
    }

    if (generation !== this.sessionGeneration) {
      return;
    }

    this.clearTokens();
    this.clearUser();
    this.setActiveSession(false);
    this.setCurrentUser(null);
    this._isAuthenticated.set(false);
    // No trustworthy roles for a logged-out principal. Reset the gate and drop
    // any in-flight hydration so a stale fetch cannot re-open it post-logout.
    this._authReady.set(false);
    this.effectivePermissionsInFlight = null;
    this.effectivePermissionsInFlightKey = null;
    this.effectivePermissionsGeneration += 1;

    // ARCH-002: Clear SharedAuthStateService for TenantStore and other consumers
    this.sharedAuthState.clearState();

    // FN-1 / SEC-007: Clear all app-specific localStorage keys to prevent stale data after logout.
    // Uses prefix-based sweep for razor_ keys plus known non-prefixed app keys.
    // GAP-FE-C013: targeted removals go through the SSR-safe wrapper; the prefix
    // sweep below still uses the raw API because key enumeration
    // (`localStorage.length` / `localStorage.key(i)`) is not exposed by
    // AppStorageService — see report.
    this.appStorage.removeItem("currentTenant");
    this.appStorage.removeItem("dashboard_activity_log");
    try {
      // Sweep all razor_ prefixed keys (covers search, layout, dashboard state, etc.)
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith("razor_")) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => this.appStorage.removeItem(key));
    } catch {
      // localStorage may be unavailable in some contexts
    }

    // The redirect is the user-visible contract of logout: whatever happened
    // during teardown, the browser MUST leave the authenticated shell. Router
    // navigation can fail two ways that both leave the operator staring at a
    // dead session — it can reject, or it can resolve `false` (a guard or a
    // competing navigation cancelled it). Both fall back to a hard location
    // replace, which cannot be vetoed.
    // Promise.resolve() normalises the return value: Router.navigate returns a
    // Promise in the app, but test doubles commonly return undefined.
    void Promise.resolve(this.router.navigate(["/auth/login"]))
      .then((navigated) => {
        if (generation !== this.sessionGeneration) {
          return;
        }
        if (navigated === false) {
          this.logger.error(
            "[AuthService] Post-logout navigation was cancelled; forcing hard redirect",
          );
          this.forceLoginRedirect();
        }
      })
      .catch((error: unknown) => {
        if (generation !== this.sessionGeneration) {
          return;
        }
        this.logger.error(
          "[AuthService] Post-logout navigation failed; forcing hard redirect",
          {
            error,
          },
        );
        this.forceLoginRedirect();
      });
  }

  /**
   * Last-resort redirect when Angular routing cannot take us to the login page.
   * `location.replace` leaves no history entry pointing back at the dead shell.
   */
  private forceLoginRedirect(): void {
    try {
      globalThis.location?.replace("/auth/login");
    } catch (error) {
      this.logger.error("[AuthService] Hard post-logout redirect failed", {
        error,
      });
    }
  }

  /** Refresh display fields after a confirmed profile read/write without changing grants. */
  updateProfileIdentity(profile: Pick<User, "id" | "username" | "firstName" | "lastName">): void {
    const current = this.currentUser();
    if (!current || current.id !== profile.id) { return; }
    this.setCurrentUser({
      ...current,
      username: profile.username,
      firstName: profile.firstName,
      lastName: profile.lastName,
    });
  }

  private setCurrentUser(user: User | null): void {
    this._currentUser.set(user);

    // Update Sentry user context for error tracking
    // SECURITY: SentryService internally hashes user ID and does NOT send email/username
    if (user) {
      this.sentryService.setUser({ id: user.id });
      // Use hashed tag for tenant ID to prevent business relationship exposure
      if (user.tenantId) {
        this.sentryService.setHashedTag("tenant", user.tenantId);
      } else {
        this.sentryService.setTag("tenant", "unscoped");
      }
    } else {
      this.sentryService.setUser(null);
    }
  }

  // Secure token management
  getToken(): string | null {
    return this.accessToken;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getCsrfToken(): string | null {
    return this.csrfToken;
  }

  /**
   * Clear all tokens from memory.
   *
   * SECURITY (DEV-1434): Access token is cleared from memory.
   * Refresh token cookie is cleared by the backend during logout (via Set-Cookie with expired date).
   * We don't store sensitive tokens in localStorage.
   */
  private clearTokens(): void {
    this.accessToken = null;
    this.csrfToken = null;
    // Note: Refresh token HttpOnly cookie is cleared by backend during logout
  }

  // Session management
  private hasActiveSession(): boolean {
    return this.appStorage.getItem(this.SESSION_KEY) === "true";
  }

  /**
   * True once no refresh can succeed anymore: the termination latch is set
   * (a logout is underway or complete) or the persisted session flag is gone
   * (e.g. another tab already logged out and swept `razor_session_active`).
   *
   * This is the same short-circuit refreshToken()/ensureAccessToken() apply
   * internally; it is public so the auth interceptor's 401-retry path can
   * fail fast instead of re-entering refreshTokenSingleFlight() for a dead
   * session — which would pointlessly acquire the cross-tab
   * `razor_token_refresh` Web Lock and replay the request token-less
   * (observed live 2026-07-05: post-logout audit-batch POSTs 401ed and each
   * still grabbed the lock).
   */
  isSessionInactive(): boolean {
    return this.sessionTerminated || !this.hasActiveSession();
  }

  private setActiveSession(active: boolean): void {
    if (active) {
      this.appStorage.setItem(this.SESSION_KEY, "true");
      // Re-arm the termination latch HERE (the single canonical "session is now
      // active" signal) so any activation path — login, MFA-verify, token
      // re-auth — releases it. A stuck latch would make every refresh return ''
      // and silently un-refresh the user.
      this.sessionTerminated = false;
    } else {
      this.appStorage.removeItem(this.SESSION_KEY);
    }
  }

  // User storage — SEC-001: Only persist non-PII identifiers needed for session rehydration.
  // Full user object (email, name, roles, permissions) stays in memory only.
  private storeUser(user: User): void {
    const minimal = { id: user.id, tenantId: user.tenantId };
    this.appStorage.setJSON(this.USER_KEY, minimal);
  }

  private getStoredUser(): User | null {
    const userJson = this.appStorage.getItem(this.USER_KEY);
    if (!userJson) {
      return null;
    }

    try {
      const parsed = JSON.parse(userJson);
      // SEC-001: localStorage stores only {id, tenantId}.
      // Construct a skeleton User with safe defaults for session rehydration.
      // Full user data (roles, permissions, email) is populated after token refresh
      // in refreshToken() → setCurrentUser().
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        typeof parsed.id === "string" &&
        (parsed.tenantId === undefined ||
          parsed.tenantId === null ||
          typeof parsed.tenantId === "string")
      ) {
        const tenantId =
          typeof parsed.tenantId === "string" && parsed.tenantId.trim()
            ? parsed.tenantId
            : null;
        return {
          id: parsed.id,
          tenantId,
          username: "",
          email: "",
          firstName: "",
          lastName: "",
          roles: [],
          permissions: [],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
      }
      this.logger.warn("[AuthService] Stored user data is malformed, clearing");
      this.clearUser();
      return null;
    } catch (error) {
      this.logger.warn(
        "[AuthService] Failed to parse stored user JSON, clearing:",
        { error },
      );
      this.clearUser();
      return null;
    }
  }

  private clearUser(): void {
    this.appStorage.removeItem(this.USER_KEY);
  }

  // SECURITY (DEV-1434): Token storage methods removed.
  // - Access tokens: stored in memory only (this.accessToken)
  // - Refresh tokens: handled exclusively via HttpOnly cookies by backend
  // This prevents XSS attacks from stealing tokens (CWE-922).

  // Utility methods
  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return !this.hasActiveSession();
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = payload.exp * 1000;
      // Pure function - no side effects
      // Token clearing should happen explicitly during logout or failed refresh
      return Date.now() >= expirationTime;
    } catch {
      // Invalid token format - treat as expired but don't clear here
      // Let the refresh cycle or logout handle token clearing
      return true;
    }
  }

  getTokenExpirationTime(): Date | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  }

  // Backward compatibility method for existing code
  isAuthenticatedUser(): boolean {
    return this.isAuthenticated();
  }

  // Method to check if refresh is needed
  needsTokenRefresh(): boolean {
    return (
      this.hasActiveSession() && (!this.accessToken || this.isTokenExpired())
    );
  }
}
